import React, { useState , useEffect} from 'react';
import { useParams, Link } from 'react-router-dom';
import Sidebar from './components/sidebar';
import axios from 'axios';
import baseUrl from '../../base_url';


const StatusDropdown = ({ returnId, returnStatus, orderStatus, onStatusUpdate }) => {
  // Combined status mapping for both models
  const statusMappings = [
    { returnStatus: "Requested", orderStatus: "Return Requested" },
    { returnStatus: "Approved", orderStatus: "Return Approved" },
    { returnStatus: "Picked Up", orderStatus: "Return in Progress" },
    { returnStatus: "Rejected", orderStatus: "Return Rejected" },
    { returnStatus: "Refunded", orderStatus: "Refunded" }
  ];
  
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Find the current status pair
  const currentStatusPair = statusMappings.find(
    mapping => mapping.returnStatus === returnStatus
  ) || statusMappings[0];
  
  const getStatusColor = (status) => {
    if (["Rejected", "Return Rejected"].includes(status)) return "red";
    if (["Picked Up", "Return Pickup Initiated"].includes(status)) return "green";
    if (["Approved", "Return Approved"].includes(status)) return "blue";
    if (["Refunded"].includes(status)) return "purple";
    return "yellow";
  };

  const handleStatusChange = async (newStatusPair) => {
    setLoading(true);
    try {
      // Call the unified API endpoint
      const response = await axios.post(
        `${baseUrl}api/admin/returns/${returnId}/update-status`,
        { 
          returnStatus: newStatusPair.returnStatus,
          orderStatus: newStatusPair.orderStatus
        },
        { withCredentials: true }
      );
      
      // If successful, update the UI
      if (response.data.success) {
        if (onStatusUpdate) {
          onStatusUpdate(newStatusPair.returnStatus, newStatusPair.orderStatus);
        }
      }
    } catch (err) {
      console.error("Error updating statuses:", err);
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`px-4 py-2 bg-white border rounded shadow-sm flex items-center justify-between min-w-64 text-${getStatusColor(
          returnStatus
        )}-600 border-${getStatusColor(returnStatus)}-300 hover:bg-${getStatusColor(
          returnStatus
        )}-50 focus:outline-none`}
      >
        <span className="font-medium">
          {loading ? 'Updating...' : `${returnStatus} / ${orderStatus || 'Not Set'}`}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 ml-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {statusMappings.map((statusPair) => (
            <button
              key={statusPair.returnStatus}
              onClick={() => handleStatusChange(statusPair)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                statusPair.returnStatus === currentStatusPair.returnStatus
                  ? `bg-${getStatusColor(statusPair.returnStatus)}-50 text-${getStatusColor(statusPair.returnStatus)}-600 font-medium`
                  : 'text-gray-700'
              }`}
            >
              <div>
                <span className="font-medium">{statusPair.returnStatus}</span>
                <span className="text-gray-500 text-sm block">Order: {statusPair.orderStatus}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReturnRequestDetails = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [returnData, setReturnData] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await axios.get(`${baseUrl}api/admin/returns/${id}`, { withCredentials: true });
        console.log(response.data.data);
        setReturnData(response.data.data);
        setLoading(false);
      } catch (err) {
        console.log(err);
        console.log("Error while fetching");
      }
    }

    fetchData();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusUpdate = (newReturnStatus, newOrderStatus) => {
    setReturnData(prev => ({ 
      ...prev, 
      status: newReturnStatus,
      orderId: { 
        ...prev.orderId, 
        returnStatus: newOrderStatus 
      } 
    }));
  };

  // Request Pickup function
  const handleRequestPickup = async () => {
    try {
      await axios.post(
        `${baseUrl}api/admin/returns/${id}/pickup-request`,
        {},
        { withCredentials: true }
      );
      // Update both statuses accordingly using our new status update handler
      handleStatusUpdate("Picked Up", "Return Pickup Initiated");
    } catch (err) {
      console.error("Error requesting pickup:", err);
    }
  };

  // Process Refund function
  const handleProcessRefund = async () => {
    try {
      await axios.post(
        `${baseUrl}api/admin/returns/${id}/process-refund`,
        {},
        { withCredentials: true }
      );
      // Update both statuses to "Refunded"
      handleStatusUpdate("Refunded", "Refunded");
      alert("Refund processed successfully");
    } catch (err) {
      console.error("Error processing refund:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-600 text-lg">LOADING .....</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1">
        <div className="p-6">
          {/* Back button and Return ID */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <Link to="/admin/returns" className="text-gray-600 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Returns
              </Link>
            </div>
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-700">Return #{returnData._id.slice(-6)}</h2>
              <div className="text-sm text-gray-600">{formatDate(returnData.requestedAt)}</div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mb-6 flex space-x-2">
            <button className="px-4 py-2 bg-white border border-gray-300 rounded flex items-center text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Print
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Return request information */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow mb-6 p-6">
                <div className="flex items-center mb-4">
                  <div className={`bg-${returnData.status === "Requested" ? "yellow" : returnData.status === "Rejected" ? "red" : "green"}-100 rounded-full p-1 mr-2`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-6 w-6 text-${returnData.status === "Requested" ? "yellow" : returnData.status === "Rejected" ? "red" : "green"}-600`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {returnData.status === "Requested" ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : returnData.status === "Rejected" ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      )}
                    </svg>
                  </div>
                  <div className="flex items-center">
                    <span className={`font-medium text-${returnData.status === "Requested" ? "yellow" : returnData.status === "Rejected" ? "red" : "green"}-600`}>
                      Status: {returnData.status}
                    </span>
                    <span className="ml-2 text-gray-500">Order #{returnData.orderId._id.slice(-6)}</span>
                  </div>
                </div>

                {/* Return reason and description */}
                <div className="mb-6 mt-4 border-t pt-4">
                  <h3 className="text-lg font-medium mb-2">Return Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Reason for Return</p>
                      <p className="font-medium text-gray-800">{returnData.reason}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Refund Method</p>
                      <p className="font-medium text-gray-800">{returnData.refundMethod}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500 text-sm mb-1">Customer Description</p>
                      <p className="text-gray-800">{returnData.description}</p>
                    </div>
                  </div>
                </div>

                {/* Return product image */}
                <div className="mb-6 border-t pt-4">
                  <h3 className="text-lg font-medium mb-2">Return Image</h3>
                  <div className="relative border rounded-lg overflow-hidden h-64 w-full flex items-center justify-center bg-gray-100">
                    {returnData.image ? (
                      <img 
                        src={returnData.image} 
                        alt="Return product" 
                        className="max-h-64 object-contain" 
                      />
                    ) : (
                      <p className="text-gray-500 italic">No image provided</p>
                    )}
                  </div>
                </div>

                <div className="mb-6 border-t pt-4">
                  <h3 className="text-lg font-medium mb-4">ShipRocket Integration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">ShipRocket Order ID</p>
                      <p className="font-medium text-gray-800">{returnData.orderId.shipRocketOrderId}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Shipment ID</p>
                      <p className="font-medium text-gray-800">{returnData.orderId.shipmentId}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Tracking Number</p>
                      <p className="font-medium text-gray-800">{returnData.orderId.tracking}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Courier</p>
                      <p className="font-medium text-gray-800">{returnData.orderId.courierName}</p>
                    </div>
                  </div>
                  <a href="#" className="text-blue-600 hover:underline flex items-center mt-2">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-4 w-4 mr-1" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Tracking
                  </a>
                </div>

                <div className="space-y-4 border-t pt-4">
                  {/* Returned product */}
                  <div className="pt-4">
                    <div className="flex">
                      <div className="flex-shrink-0 mr-4 relative">
                        <img
                          src={returnData.productId.image[0].src}
                          alt="Product"
                          className="w-16 h-16 object-cover border rounded"
                        />
                        <div className="absolute -top-2 -left-2 bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                          1
                        </div>
                      </div>
                      <div className="flex-grow">
                        <a href="#" className="text-blue-500">
                          {returnData.productId.title}
                        </a>
                        <p className="text-xs text-gray-500 mt-1">Article Code: {returnData.productId.articleCode}</p>
                        <div className="flex justify-between mt-2">
                          <div className="flex items-center">
                            <span className="text-gray-700">₹ {returnData.productId.numericPrice}</span>
                            <span className="mx-2 text-gray-500">×</span>
                            <span className="text-gray-700">1</span>
                          </div>
                          <span className="text-gray-700">₹ {returnData.productId.numericPrice}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-lg font-medium mb-4">Status Management</h3>
                  <div className="mb-4">
                    <p className="text-gray-500 text-sm mb-2">Return & Order Status</p>
                    <StatusDropdown 
                      returnId={id}
                      returnStatus={returnData.status}
                      orderStatus={returnData.orderId.returnStatus || "Return Requested"}
                      onStatusUpdate={handleStatusUpdate}
                    />
                  </div>
                  
                  <div className="flex space-x-2 mt-4">
                    <button 
                      onClick={handleRequestPickup}
                      className="px-4 py-2 bg-blue-600 text-white border border-blue-600 rounded hover:bg-blue-700"
                    >
                      Request Pickup
                    </button>
                    <button 
                      onClick={handleProcessRefund}
                      className="px-4 py-2 bg-green-600 text-white border border-green-600 rounded hover:bg-green-700"
                    >
                      Process Refund
                    </button>
                  </div>
                </div>
              </div>
            </div>

            

            {/* Customer information */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-gray-700 font-medium">Customer</h3>
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-700 mb-1">{returnData.orderId.address[0].fullName}</p>
                <p className="text-gray-600">{returnData.orderId.address[0].phone}</p>

                <div className="mt-6">
                  <h4 className="text-gray-700 font-medium mb-2">CONTACT INFORMATION</h4>
                  <p className="text-gray-600 mb-6">Customer ID: {returnData.userId.slice(-8)}</p>

                  <h4 className="text-gray-700 font-medium mb-2">SHIPPING ADDRESS</h4>
                  <p className="text-gray-600">
                    {returnData.orderId.address[0].fullName}<br />
                    {returnData.orderId.address[0].houseNumber} {returnData.orderId.address[0].streetAddress}<br />
                    {returnData.orderId.address[0].city}, {returnData.orderId.address[0].state} {returnData.orderId.address[0].postalCode}<br />
                    {returnData.orderId.address[0].country}
                  </p>

                  <h4 className="text-gray-700 font-medium mt-6 mb-2">RETURN TIMELINE</h4>
                  <div className="space-y-3">
                    <div className="flex">
                      <div className="mr-2 w-6 flex justify-center">
                        <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Return Requested</p>
                        <p className="text-xs text-gray-500">{formatDate(returnData.requestedAt)}</p>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="mr-2 w-6 flex justify-center">
                        <div className={`h-6 w-6 rounded-full ${returnData.status !== "Requested" ? "bg-green-100" : "bg-gray-100"} flex items-center justify-center`}>
                          <svg xmlns="http://www.w3.org/2000/svg" 
                               className={`h-4 w-4 ${returnData.status !== "Requested" ? "text-green-600" : "text-gray-400"}`} 
                               fill="none" 
                               viewBox="0 0 24 24" 
                               stroke="currentColor">
                            {returnData.status === "Rejected" ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            )}
                          </svg>
                        </div>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${returnData.status !== "Requested" ? "" : "text-gray-500"}`}>
                          {returnData.status === "Rejected" ? "Return Rejected" : 
                           returnData.status === "Approved" ? "Return Approved" : 
                           returnData.status === "Picked Up" ? "Pickup Scheduled" : "Awaiting Approval"}
                        </p>
                        {returnData.status !== "Requested" && (
                          <p className="text-xs text-gray-500">
                            {/* Here we could display the timestamp when status was updated */}
                            {returnData.updatedAt ? formatDate(returnData.updatedAt) : ""}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-700 font-medium mb-4">Order Summary</h3>
                <div className="space-y-2 border-b pb-4 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Total</span>
                    <span className="text-gray-800">₹ {returnData.orderId.totalAmount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method</span>
                    <span className="text-gray-800">{returnData.orderId.paymentMethod[0]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Date</span>
                    <span className="text-gray-800">{new Date(returnData.orderId.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Date</span>
                    <span className="text-gray-800">{new Date(returnData.orderId.deliveryDate).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-gray-800">Refund Amount</span>
                  <span className="text-gray-800">₹ {returnData.productId.numericPrice}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default ReturnRequestDetails;